/**
 * Eaze plan prices in the visitor's / business's own currency (13d).
 *
 * aeTech sets a fixed price list for every country Eaze sells in. The
 * figure shown is always their own money (lib/plan-pricing): their list
 * price, or ≈ the GHS price at today's rate when no list is set. When the
 * charge goes through another currency, one small line says so.
 *
 *   usePlanPricing(override?)  → { local, billing, chargeable, plans, price(plan, cycle) }
 *   <PlanPrice plan={plan} cycle="monthly" />
 *
 * Signed in: the open business's currency. Signed out: the visitor's
 * country (lib/visitor-locale), or the country picked on sign-up.
 */
import React from "react";
import type { BillingCycle } from "@/constants/plans";
import { currencyPlural, formatPlanPrice, type PricedPlan } from "@/lib/plan-pricing";
import { usePlanPricing } from "@/hooks/usePlanPricing";

export const PlanPrice: React.FC<{
  plan: PricedPlan;
  cycle: BillingCycle;
  size?: "lg" | "md" | "sm";
  className?: string;
  /** Dark card (white text). */
  inverse?: boolean;
}> = ({ plan, cycle, size = "md", className = "", inverse = false }) => {
  const { price } = usePlanPricing();
  const p = price(plan, cycle);
  const per = cycle === "monthly" ? "mo" : "yr";
  const big = size === "lg" ? "text-4xl" : size === "md" ? "text-xl" : "text-sm";
  const main = inverse ? "text-white" : "text-gray-900 dark:text-foreground";
  const muted = inverse ? "text-white/60" : "text-gray-500 dark:text-muted-foreground";
  return (
    <span className={`inline-flex flex-col ${className}`}>
      <span className="inline-flex items-baseline gap-1">
        <span className={`${big} font-bold tabular-nums ${main}`}>{formatPlanPrice(p)}</span>
        <span className={`text-sm ${muted}`}>/{per}</span>
      </span>
      {p.chargeNote && <span className={`text-[11px] ${muted}`}>{p.chargeNote}</span>}
    </span>
  );
};

export default PlanPrice;

/**
 * One line for billing screens when the charge goes through another
 * currency than the one they see (their own). `currency` = what the server
 * says it's charged in (a quote / their plan), else what the rules imply.
 */
export const PlanCurrencyNote: React.FC<{ className?: string; currency?: string | null }> = ({ className = "", currency }) => {
  const { local, billing } = usePlanPricing();
  const charged = currency || billing;
  if (charged === local) return null;
  return (
    <p className={`rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground ${className}`}>
      Prices are in {currencyPlural(local)}. Payments for your country go through in {currencyPlural(charged)} for now, at today's rate — your bank or mobile money converts, so you pay about the price shown.
    </p>
  );
};
